/**
 * ExecutiveView — Direction A "CRM Operator" UI.
 *
 * Decision A5 enforcement (Wave G, closes B-EXEC1):
 * Executive surfaces aggregate at the team level only. Per-rep names,
 * per-rep metrics, and rep-level drill-downs are forbidden on this surface.
 * Manager-only drill-down is the correct gate for individual rep data.
 *
 * Legacy implementation preserved at ExecutiveView.legacy.tsx.bak.
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { buildDrillUrl } from "@/lib/drillUrl";
import OperatorShell from "@/components/operator/OperatorShell";
import OperatorKpiCard from "@/components/operator/OperatorKpiCard";
import OperatorRightRail, { RailCard, PipelineByStage, ActivityFeed } from "@/components/operator/OperatorRightRail";
import ConversionRateBand from "@/components/dashboards/ConversionRateBand";
import { tokens } from "@/components/operator/tokens";
import { supabase, getSession, getCurrentTenant } from "@/lib/session";
import ExecutiveCp10Aggregates from "@/components/dashboards/ExecutiveCp10Aggregates";
import type { DealForecastInput, DeclaredTiming } from "@/lib/forecast-rules";
import { PIPELINE_COVERAGE_MULTIPLIER } from "@shared/schema";

// Team-level aggregates only — Decision A5 forbids per-rep granularity here.
interface TeamRollup {
  aeCount: number;
  totalQuota: number;
  totalClosed: number;
  totalWeighted: number;
  totalAccounts: number;
  aeMissingPlan: number;
  aeBelowCoverage: number;
}

// Tenant 2 (Mi6 Demo) sample data — team totals only, no individual names.
const DEMO_ROLLUP: TeamRollup = {
  aeCount: 4,
  totalQuota: 5400000,
  totalClosed: 1605000,
  totalWeighted: 2330000,
  totalAccounts: 47,
  aeMissingPlan: 0,
  aeBelowCoverage: 1,
};

// Demo deal distribution for CP-10 aggregates block.
const DEMO_DEALS: DealForecastInput[] = [
  { compositePct: 84, timing: 90 }, { compositePct: 78, timing: 90 },
  { compositePct: 72, timing: 80 }, { compositePct: 70, timing: 70 },
  { compositePct: 66, timing: 70 }, { compositePct: 64, timing: 60 },
  { compositePct: 58, timing: 60 }, { compositePct: 55, timing: 50 },
  { compositePct: 48, timing: 50 }, { compositePct: 41, timing: 0 },
  { compositePct: 38, timing: 0 },  { compositePct: 88, timing: 90 },
];

function fmtMoney(n: number): string {
  if (!isFinite(n) || n === 0) return "$0";
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${Math.round(n)}`;
}

interface Props {
  loading?: boolean;
}

export default function ExecutiveView(_: Props = {}) {
  const session = getSession();
  const TENANT_ID = session?.tenantId ?? 2;
  const tenant = getCurrentTenant();
  const isDemo = tenant?.is_demo === true;
  const [, setLocation] = useLocation();

  const [rollup, setRollup] = useState<TeamRollup | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const sb = supabase();
        const year = new Date().getFullYear();
        // Decision A5: fetch only aggregated team data — no individual rep names returned.
        const { data: usersData } = await sb
          .from("users")
          .select("id, role, tenant_id")
          .eq("tenant_id", TENANT_ID)
          .eq("role", "ae");
        const aeIds = (usersData ?? []).map((u: any) => u.id);
        const { data: planData } = aeIds.length
          ? await sb.from("sales_plans").select("ae_user_id, annual_quota").eq("tenant_id", TENANT_ID).eq("plan_year", year).in("ae_user_id", aeIds)
          : { data: [] };
        const { data: pipeData } = aeIds.length
          ? await sb.from("v_ae_pipeline_summary").select("ae_user_id, weighted_pipeline, won_value").eq("tenant_id", TENANT_ID).in("ae_user_id", aeIds)
          : { data: [] };
        const { data: accData } = aeIds.length
          ? await sb.from("accounts").select("id").eq("tenant_id", TENANT_ID).in("owner_user_id", aeIds)
          : { data: [] };

        // Aggregate to team totals — no per-AE breakdown surfaced.
        let totalQuota = 0;
        let totalClosed = 0;
        let totalWeighted = 0;
        let aeMissingPlan = 0;
        let aeBelowCoverage = 0;
        for (const u of (usersData ?? []) as any[]) {
          const plan = ((planData ?? []) as any[]).find((p) => p.ae_user_id === u.id);
          const summary = ((pipeData ?? []) as any[]).find((p) => p.ae_user_id === u.id);
          const quota = Number(plan?.annual_quota ?? 0);
          const closed = Number(summary?.won_value ?? 0);
          const weighted = Number(summary?.weighted_pipeline ?? 0);
          const forecast = closed + weighted;
          totalQuota += quota;
          totalClosed += closed;
          totalWeighted += weighted;
          if (quota === 0) aeMissingPlan++;
          if (quota > 0 && forecast / quota < PIPELINE_COVERAGE_MULTIPLIER) aeBelowCoverage++;
        }

        if (!cancelled) {
          setRollup({
            aeCount: aeIds.length,
            totalQuota,
            totalClosed,
            totalWeighted,
            totalAccounts: (accData ?? []).length,
            aeMissingPlan,
            aeBelowCoverage,
          });
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setRollup(null);
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [TENANT_ID]);

  const usingDemo = rollup === null && isDemo;
  const data: TeamRollup = rollup ?? (isDemo ? DEMO_ROLLUP : {
    aeCount: 0, totalQuota: 0, totalClosed: 0, totalWeighted: 0,
    totalAccounts: 0, aeMissingPlan: 0, aeBelowCoverage: 0,
  });

  const totalForecast = data.totalClosed + data.totalWeighted;
  // Track D patch (2026-05-13): removed hardcoded $5M floor for real tenants.
  const annualObjective = usingDemo ? 5_000_000 : data.totalQuota;
  const gap = Math.max(0, annualObjective - totalForecast);
  const coverage = annualObjective > 0 ? (totalForecast / annualObjective).toFixed(2) : "—";

  // Coverage-vs-objective bars using canon colors.
  // These are NOT forecast bucket bars — they show Forecast / Closed / Gap
  // against the annual objective. Colors: green / green / red.
  const pipelineByStage = [
    { stage: "Forecast",  count: Math.round(data.totalWeighted / 50000),  color: "#16A34A" }, // green
    { stage: "Closed",    count: Math.round(data.totalClosed   / 50000),  color: "#16A34A" }, // green
    { stage: "Gap",       count: Math.round(gap                / 50000),  color: "#DC2626" }, // red
  ];

  // RULE-FORECAST-1 portfolio rollup — team-level deal distribution.
  // Decision A5: synthesized from team totals, not per-rep breakdown.
  const cp10Deals: DealForecastInput[] = useMemo(() => {
    if (usingDemo) return DEMO_DEALS;
    const n = Math.min(80, Math.max(0, data.totalAccounts));
    const t: DeclaredTiming = Number(coverage) >= PIPELINE_COVERAGE_MULTIPLIER ? 90
      : Number(coverage) >= 1.8 ? 80
      : Number(coverage) >= 1.2 ? 70
      : Number(coverage) > 0 ? 60 : 50;
    return Array.from({ length: n }, () => ({ compositePct: 60, timing: t }));
  }, [data.totalAccounts, coverage, usingDemo]);

  const escalations = usingDemo ? 4 : 0;

  return (
    <OperatorShell
      breadcrumbs={[
        { label: "Home", href: "/" },
        { label: "Dashboards", href: "/dashboards" },
        { label: "Executive" },
      ]}
      primaryAction={{ label: "+ Add Account", href: "/accounts/new" }}
      pageTitle="Executive view"
      pageMeta="Company-wide pipeline and plan vs actual — team rollup."
    >
      <ConversionRateBand scope="team" role="exec" />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 16,
          marginBottom: 16,
        }}
      >
        {/* BUILD-14: KPI card drill-downs */}
        <OperatorKpiCard
          label="Annual Objective"
          value={fmtMoney(annualObjective)}
          hint="Top-down target"
        />
        <div
          onClick={() => setLocation(buildDrillUrl('/pipeline', { stage: 'Negotiation', from: '/dashboards/executive', fromLabel: 'Executive Dashboard' }))}
          style={{ cursor: 'pointer' }}
          title="Click to view closed pipeline"
        >
        <OperatorKpiCard
          label="Booked YTD"
          value={fmtMoney(data.totalClosed)}
          delta={{ value: `${Math.round((data.totalClosed / Math.max(1, annualObjective)) * 100)}% of objective — click to view`, direction: "up", tone: "good" }}
          progress={{ pct: (data.totalClosed / Math.max(1, annualObjective)) * 100, color: tokens.color.success }}
        />
        </div>
        <div
          onClick={() => setLocation('/forecast')}
          style={{ cursor: 'pointer' }}
          title="Click to view full forecast breakdown"
        >
        <OperatorKpiCard
          label="Forecast"
          value={fmtMoney(totalForecast)}
          delta={{ value: `${coverage}x coverage — click to view`, direction: Number(coverage) >= 1 ? "up" : "down", tone: Number(coverage) >= 1 ? "good" : "bad" }}
        />
        </div>
        <div
          onClick={() => setLocation(buildDrillUrl('/pipeline', { gap: '1', from: '/dashboards/executive', fromLabel: 'Executive Dashboard' }))}
          style={{ cursor: 'pointer' }}
          title="Click to view gap accounts"
        >
        <OperatorKpiCard
          label="Gap to Objective"
          value={fmtMoney(gap)}
          delta={{ value: gap === 0 ? "Covered" : "Needs sourcing — click to view", direction: gap === 0 ? "up" : "down", tone: gap === 0 ? "good" : "bad" }}
        />
        </div>
      </div>

      <ExecutiveCp10Aggregates deals={cp10Deals} escalations={escalations} />

      {/* Decision A5: team-level summary only — no per-rep table or drill-down. */}
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            background: tokens.color.surface,
            border: `1px solid ${tokens.color.border}`,
            borderRadius: 8,
            padding: 20,
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: tokens.color.textMuted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 16 }}>
              Team Pipeline Rollup
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 16 }}>
              <div>
                <div style={{ fontSize: 11, color: tokens.color.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>AEs on Team</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: tokens.color.textPrimary, marginTop: 4 }}>{loading ? "—" : data.aeCount}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: tokens.color.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>Team Quota</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: tokens.color.textPrimary, marginTop: 4 }}>{loading ? "—" : fmtMoney(data.totalQuota)}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: tokens.color.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>Weighted Pipeline</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: tokens.color.textPrimary, marginTop: 4 }}>{loading ? "—" : fmtMoney(data.totalWeighted)}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: tokens.color.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>Active Accounts</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: tokens.color.textPrimary, marginTop: 4 }}>{loading ? "—" : data.totalAccounts}</div>
              </div>
            </div>
            {/* Per-rep breakdown is available to Manager role only — Decision A5. */}
            <div style={{ marginTop: 16, fontSize: 12, color: tokens.color.textMuted }}>
              Individual rep metrics are available in the Manager dashboard.
            </div>
          </div>
        </div>
        <OperatorRightRail>
          {/* BUILD-14: coverage composition bars drill into pipeline */}
          <RailCard title="Coverage Composition" footer="50K = 1 unit shown">
            <PipelineByStage
              rows={pipelineByStage}
              onRowClick={(stage) => {
                if (stage === "Pipeline") setLocation(buildDrillUrl('/pipeline', { from: '/dashboards/executive', fromLabel: 'Executive Dashboard' }));
                else if (stage === "Closed") setLocation(buildDrillUrl('/pipeline', { stage: 'Negotiation', from: '/dashboards/executive', fromLabel: 'Executive Dashboard' }));
                else if (stage === "Gap") setLocation(buildDrillUrl('/pipeline', { gap: '1', from: '/dashboards/executive', fromLabel: 'Executive Dashboard' }));
              }}
            />
          </RailCard>
          <RailCard title="Risk Signals">
            <ActivityFeed
              items={[
                { text: `${data.aeMissingPlan} of ${data.aeCount} AEs missing a sales plan`, sub: "Settings", color: data.aeMissingPlan > 0 ? tokens.color.danger : tokens.color.success },
                { text: `${data.aeBelowCoverage} of ${data.aeCount} AEs below ${PIPELINE_COVERAGE_MULTIPLIER}X coverage`, sub: "Forecast risk", color: tokens.color.amber },
                // Track D rebuild patch (2026-05-14): removed hardcoded "Q-end forecast
                // review scheduled / Friday" — fabricated calendar entry shown to all
                // tenants. Replaced with honest gap signal derived from real data.
                ...(gap > 0
                  ? [{ text: `${fmtMoney(gap)} gap to annual objective`, sub: "Awaiting first entry", color: tokens.color.info }]
                  : [{ text: "On track to meet annual objective", sub: "Live data", color: tokens.color.success }]
                ),
              ]}
            />
          </RailCard>
        </OperatorRightRail>
      </div>
    </OperatorShell>
  );
}
